import { useEffect, useRef } from "react";
import type { NativeStackScreenProps } from "@react-navigation/native-stack";

import { useCountryCode, useTranslations } from "@src/packages/i18n";
import {
  useOpenedVialEvidence,
  useUpdateMedication,
  vialEvidenceMatters,
} from "@src/packages/care";
import { usePatientMedications } from "@src/packages/care/hooks/usePatientMedications";
import { useMedicationFormCorrection } from "@src/packages/care/useMedicationFormCorrection";
import {
  offersCoaching,
  resolveCoachAvailability,
  resolveCoachProtocols,
} from "@src/packages/injection-coach/availability";
import { useInjectionCoach } from "@src/packages/injection-coach/components/InjectionCoachHost";

import type { RootStackParamList } from "../../App";
import { useSmartBack } from "../hooks/useSmartBack";
import {
  medicationDoseLabel,
  medicationName,
} from "./care/suggested/conceptCalendarData";

type Props = NativeStackScreenProps<RootStackParamList, "InjectionCoach">;

/**
 * The open injection coach, as a URL.
 *
 * The coach itself is not here: it is mounted once above the navigator by
 * `InjectionCoachProvider`, and this screen is a transparent route over the
 * surface that opened it. All it does is keep the address bar and the coach
 * agreeing with each other:
 *
 * - Opening the coach from an entry point pushes this route (`App.tsx`'s
 *   `onOpened`), so `/coach/:patientMedicationId` is in the URL while it is up.
 * - When the coach closes itself — the X, "Stop for now", finishing — this
 *   route leaves the way any pushed screen does (`useSmartBack`).
 * - When this route leaves first — the browser's back button, Android's — it
 *   closes the coach, so no walk is left floating over a screen that has moved.
 * - On a cold load (a reload on web), nothing is open, so the request is
 *   rebuilt from her medication record the way her Home list builds it. A
 *   record that cannot be read, or that has no walk, leaves the route rather
 *   than opening a coach with nothing behind it.
 *
 * `onFinished` cannot survive a reload — it belongs to the surface that opened
 * the coach — so a rebuilt walk ends by closing, which the host already does
 * when none is given.
 */
export default function InjectionCoachScreen({ navigation, route }: Props) {
  const patientMedicationId = route.params?.patientMedicationId ?? null;
  /** A reference walk from a factsheet, which has no record behind it. */
  const referenceName = route.params?.medication ?? null;

  const t = useTranslations();
  const countryCode = useCountryCode();
  const coach = useInjectionCoach();
  const leave = useSmartBack({ fallbackTab: "Home" });

  const medications = usePatientMedications({
    enabled: Boolean(patientMedicationId),
  });
  const record = patientMedicationId
    ? (medications.data?.find((med) => med.id === patientMedicationId) ?? null)
    : null;
  const recordName = record ? medicationName(record, t) : null;

  // The same write-backs her Home row offers, so a rebuilt walk learns what an
  // opened one would. Hooks, so built whether or not a walk is rebuilt.
  const correctMedicationForm = useMedicationFormCorrection(
    record?.id,
    record?.form,
  );
  const updateMedication = useUpdateMedication();
  const hasEarlierDose = useOpenedVialEvidence(record?.id, {
    enabled: vialEvidenceMatters(
      resolveCoachProtocols(recordName ?? "")?.protocols ?? [],
    ),
  });

  const coachOpen = coach?.isOpen ?? false;
  /** The coach has been open while this route was up. */
  const seenOpen = useRef(false);
  /** A cold load has been answered, by opening the coach or by leaving. */
  const settled = useRef(false);
  /** Once only: a second `leave()` would pop the screen underneath too. */
  const left = useRef(false);

  useEffect(() => {
    if (!coach) return;
    const exit = () => {
      if (left.current) return;
      left.current = true;
      leave();
    };
    if (coachOpen) {
      seenOpen.current = true;
      return;
    }
    // It was open and now is not: the coach closed itself.
    if (seenOpen.current) {
      exit();
      return;
    }
    if (settled.current) return;

    if (!patientMedicationId) {
      settled.current = true;
      const hasWalk =
        referenceName !== null &&
        (resolveCoachProtocols(referenceName, countryCode)?.protocols.length ??
          0) > 0;
      if (!referenceName || !hasWalk) {
        exit();
        return;
      }
      coach.open({ medicationName: referenceName });
      return;
    }

    // Still reading. A query that is pending but idle is one that cannot run —
    // nobody is signed in — and is answered below like a missing record.
    if (medications.isPending && medications.fetchStatus !== "idle") return;
    settled.current = true;

    if (!record || !recordName) {
      exit();
      return;
    }
    const availability = resolveCoachAvailability({
      routes: [record.route],
      medicationName: recordName,
      countryCode,
    });
    if (!offersCoaching(availability)) {
      exit();
      return;
    }
    const recordId = record.id;
    coach.open({
      medicationName: recordName,
      prescribedDose: medicationDoseLabel(record) ?? null,
      patientMedicationId: recordId,
      prescribedForm: record.form ?? null,
      hasEarlierDose,
      onFormCorrected: correctMedicationForm,
      onCorrectDose: (dose) =>
        updateMedication.mutate({
          medicationId: recordId,
          dose: dose.value,
          doseUnit: dose.unit,
        }),
    });
  }, [
    coach,
    coachOpen,
    leave,
    patientMedicationId,
    referenceName,
    countryCode,
    medications.isPending,
    medications.fetchStatus,
    record,
    recordName,
    hasEarlierDose,
    correctMedicationForm,
    updateMedication,
  ]);

  // The vial record can land after the walk opened; the entry point hands it
  // over the same way.
  const noteEarlierDose = coach?.noteEarlierDose;
  useEffect(() => {
    if (!noteEarlierDose || !patientMedicationId) return;
    noteEarlierDose(patientMedicationId, hasEarlierDose);
  }, [noteEarlierDose, patientMedicationId, hasEarlierDose]);

  // Leaving first — the browser's back button, Android's — closes the coach.
  // On `beforeRemove` rather than an unmount cleanup, so it answers the one
  // event it is about — this route being taken off the stack — and not a
  // remount. Through refs so the listener never has to be rebuilt mid-walk.
  const openRef = useRef(coachOpen);
  const closeRef = useRef(coach?.close);
  useEffect(() => {
    openRef.current = coachOpen;
    closeRef.current = coach?.close;
  }, [coachOpen, coach]);
  useEffect(
    () =>
      navigation.addListener("beforeRemove", () => {
        if (openRef.current) closeRef.current?.();
      }),
    [navigation],
  );

  return null;
}
